import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { 
  FileText, Download, TrendingUp, Users, IndianRupee, Calendar,
  RefreshCw, BarChart3, PieChart, LineChart, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('center_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CenterReportsPage = () => {
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [reportData, setReportData] = useState({
    revenue: { total: 0, growth: 0 },
    students: { total: 0, new: 0, growth: 0 },
    attendance: { avg: 0 },
    courses: []
  });

  useEffect(() => {
    fetchReports();
  }, [selectedPeriod]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/center/reports?period=${selectedPeriod}`, { headers: getAuthHeaders() });
      if (res.data) {
        setReportData(res.data);
      } else {
        // Default empty data
        setReportData({
          revenue: { total: 0, growth: 0 },
          students: { total: 0, new: 0, growth: 0 },
          attendance: { avg: 0 },
          courses: []
        });
      }
    } catch (error) {
      console.error('Fetch reports error:', error);
      setReportData({
        revenue: { total: 0, growth: 0 },
        students: { total: 0, new: 0, growth: 0 },
        attendance: { avg: 0 },
        courses: []
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = (type) => {
    if (!reportData) { toast.info('No data to export'); return; }
    let csv = '';
    let filename = `${type}_report_${selectedPeriod}.csv`;
    
    if (type === 'revenue' || type === 'finance' || type === 'finance_report') {
      csv = 'Date,Amount (₹),Transactions\n';
      (reportData.revenue_trend || []).forEach(t => {
        csv += `${t.date},${t.amount},${t.count}\n`;
      });
      csv += `\nTotal Revenue (All-time),${reportData.revenue.total},\nThis ${selectedPeriod},${reportData.revenue.period},\nGrowth %,${reportData.revenue.growth},\n`;
    } else if (type === 'students' || type === 'student_report') {
      csv = 'Course,Students Enrolled\n';
      (reportData.distribution || []).forEach(d => {
        csv += `"${d.course}",${d.count}\n`;
      });
      csv += `\nTotal Students,${reportData.students.total},\nNew (this period),${reportData.students.new},\n`;
    } else if (type === 'courses' || type === 'course_report') {
      csv = 'Course,Students,Active,Revenue,Expected Revenue,Completion %\n';
      (reportData.courses || []).forEach(c => {
        csv += `"${c.name}",${c.students},${c.active || 0},${c.revenue || 0},${c.expected_revenue || 0},${c.completion}\n`;
      });
    } else if (type === 'attendance_report') {
      csv = 'Metric,Value\n';
      csv += `Average Attendance,${reportData.attendance.avg}%\n`;
      csv += `Total Students,${reportData.students.total}\n`;
    } else {
      toast.info('Report type not available');
      return;
    }
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="center-reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports & Analytics</h1>
          <p className="text-slate-500 mt-1">Track your center's performance</p>
        </div>
        <div className="flex gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg text-sm bg-white"
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
          <Button onClick={fetchReports} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div 
          whileHover={{ scale: 1.02 }}
          className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-5 text-white shadow-lg"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-emerald-100 text-sm">Total Revenue</p>
              <p className="text-3xl font-bold mt-1">₹{reportData.revenue.total.toLocaleString()}</p>
              <div className="flex items-center gap-1 mt-2 text-sm">
                <ArrowUpRight className="w-4 h-4" />
                <span>+{reportData.revenue.growth}%</span>
              </div>
            </div>
            <IndianRupee className="w-8 h-8 text-emerald-200" />
          </div>
        </motion.div>

        <motion.div 
          whileHover={{ scale: 1.02 }}
          className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white shadow-lg"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total Students</p>
              <p className="text-3xl font-bold mt-1">{reportData.students.total}</p>
              <div className="flex items-center gap-1 mt-2 text-sm">
                <ArrowUpRight className="w-4 h-4" />
                <span>+{reportData.students.new} new</span>
              </div>
            </div>
            <Users className="w-8 h-8 text-blue-200" />
          </div>
        </motion.div>

        <motion.div 
          whileHover={{ scale: 1.02 }}
          className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-5 text-white shadow-lg"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-purple-100 text-sm">Avg Attendance</p>
              <p className="text-3xl font-bold mt-1">{reportData.attendance.avg}%</p>
              <div className="w-full h-2 bg-white/30 rounded-full mt-2 overflow-hidden">
                <div 
                  className="h-full bg-white rounded-full"
                  style={{ width: `${reportData.attendance.avg}%` }}
                />
              </div>
            </div>
            <Calendar className="w-8 h-8 text-purple-200" />
          </div>
        </motion.div>

        <motion.div 
          whileHover={{ scale: 1.02 }}
          className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-5 text-white shadow-lg"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-amber-100 text-sm">Growth Rate</p>
              <p className="text-3xl font-bold mt-1">+{reportData.students.growth}%</p>
              <div className="flex items-center gap-1 mt-2 text-sm">
                <TrendingUp className="w-4 h-4" />
                <span>vs last period</span>
              </div>
            </div>
            <TrendingUp className="w-8 h-8 text-amber-200" />
          </div>
        </motion.div>
      </div>

      {/* Charts Section */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <LineChart className="w-5 h-5 text-emerald-500" />
              Revenue Trend
            </h3>
            <Button variant="outline" size="sm" onClick={() => downloadReport('revenue')} className="gap-1">
              <Download className="w-4 h-4" />
              Export
            </Button>
          </div>
          <div className="h-64 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100/50 p-3">
            {(reportData.revenue_trend || []).length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <LineChart className="w-12 h-12 text-emerald-300 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">No revenue data for this period</p>
                  <p className="text-emerald-600 font-bold mt-1">All-time: ₹{reportData.revenue.total.toLocaleString()}</p>
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col">
                <div className="flex items-end gap-1 h-full pb-6">
                  {(() => {
                    const trend = reportData.revenue_trend.slice(-30);
                    const max = Math.max(...trend.map(t => t.amount), 1);
                    return trend.map((t, i) => (
                      <div key={i} className="flex-1 flex flex-col justify-end items-center group min-w-[8px]" title={`${t.date}: ₹${t.amount.toLocaleString()}`}>
                        <div className="w-full bg-gradient-to-t from-emerald-600 to-emerald-400 rounded-t hover:from-emerald-700 hover:to-emerald-500 transition-all"
                          style={{ height: `${(t.amount / max) * 90}%`, minHeight: '4px' }}></div>
                      </div>
                    ));
                  })()}
                </div>
                <div className="flex justify-between text-[10px] text-slate-500 px-1 mt-1">
                  <span>{reportData.revenue_trend[0]?.date}</span>
                  <span className="font-bold text-emerald-700">Total: ₹{reportData.revenue.period.toLocaleString()}</span>
                  <span>{reportData.revenue_trend[reportData.revenue_trend.length-1]?.date}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Student Distribution */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-blue-500" />
              Student Distribution
            </h3>
            <Button variant="outline" size="sm" onClick={() => downloadReport('students')} className="gap-1">
              <Download className="w-4 h-4" />
              Export
            </Button>
          </div>
          <div className="h-64 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100/50 p-3">
            {(reportData.distribution || []).length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <PieChart className="w-12 h-12 text-blue-300 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">No student data</p>
                </div>
              </div>
            ) : (
              <div className="h-full overflow-y-auto space-y-2 pr-1">
                {(() => {
                  const total = reportData.distribution.reduce((s, d) => s + d.count, 0);
                  const palette = ['bg-blue-500', 'bg-purple-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-cyan-500', 'bg-violet-500', 'bg-orange-500'];
                  return reportData.distribution.map((d, i) => {
                    const pct = total > 0 ? (d.count / total) * 100 : 0;
                    return (
                      <div key={i} className="bg-white rounded-lg p-2.5">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold text-slate-700 truncate flex-1">{d.course}</span>
                          <span className="text-xs font-bold text-slate-900 ml-2">{d.count} ({pct.toFixed(1)}%)</span>
                        </div>
                        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${palette[i % palette.length]}`} style={{ width: `${pct}%` }}></div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Course Performance Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-500" />
            Course Performance
          </h3>
          <Button variant="outline" size="sm" onClick={() => downloadReport('courses')} className="gap-1">
            <Download className="w-4 h-4" />
            Export All
          </Button>
        </div>
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Course</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Students</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Revenue</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Completion</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Performance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {reportData.courses.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-4 py-8 text-center text-sm text-slate-400">No course data available</td>
              </tr>
            ) : reportData.courses.map((course, idx) => (
              <tr key={idx} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">{course.name}</td>
                <td className="px-4 py-3 text-slate-600">{course.students}</td>
                <td className="px-4 py-3">
                  <span className="font-semibold text-emerald-600">₹{course.revenue.toLocaleString()}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          course.completion >= 80 ? 'bg-emerald-500' :
                          course.completion >= 60 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${course.completion}%` }}
                      />
                    </div>
                    <span className="text-sm text-slate-600">{course.completion}%</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    course.completion >= 80 ? 'bg-emerald-100 text-emerald-700' :
                    course.completion >= 60 ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {course.completion >= 80 ? 'Excellent' : course.completion >= 60 ? 'Good' : 'Needs Attention'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Quick Download Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        {[
          { title: 'Student Report', icon: Users, color: 'blue' },
          { title: 'Finance Report', icon: IndianRupee, color: 'emerald' },
          { title: 'Attendance Report', icon: Calendar, color: 'purple' },
          { title: 'Course Report', icon: FileText, color: 'amber' },
        ].map((report, idx) => (
          <motion.div
            key={idx}
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm cursor-pointer hover:border-purple-300"
            onClick={() => downloadReport(report.title.toLowerCase().replace(' ', '_'))}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 bg-${report.color}-100 rounded-lg flex items-center justify-center`}>
                <report.icon className={`w-5 h-5 text-${report.color}-600`} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-slate-900">{report.title}</p>
                <p className="text-xs text-slate-500">Download PDF</p>
              </div>
              <Download className="w-4 h-4 text-slate-400" />
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default CenterReportsPage;
